import { supabase } from '../lib/supabase'; // Using your original import path

// Transform raw quote data from database to frontend format
const transformQuoteData = (rawQuote) => {
  if (!rawQuote) return null;
  
  return {
    id: rawQuote.id,
    uniqueId: rawQuote.unique_id,
    clientName: rawQuote.client_name,
    clientEmail: rawQuote.client_email,
    status: rawQuote.status,
    createdAt: rawQuote.created_at,
    expiresAt: rawQuote.expires_at,
    interactiveEnabled: rawQuote.interactive_enabled,
    interactiveTotal: rawQuote.interactive_total,
    selectedTier: rawQuote.selected_tier,
    clientSignatureName: rawQuote.client_signature_name,
    signedAt: rawQuote.signed_at,
    acceptedAt: rawQuote.accepted_at,
    userId: rawQuote.user_id,
    
    // Include view tracking data
    viewCount: rawQuote.view_count || 0,
    firstViewedAt: rawQuote.first_viewed_at,
    lastViewedAt: rawQuote.last_viewed_at,
    viewedAtList: rawQuote.viewed_at_list || [], // ← NEW: Individual timestamps
    
    // HVAC P0: Monthly payment data
    showMonthlyPayments: rawQuote.show_monthly_payments || false,
    financingApr: rawQuote.financing_apr || 0.00,
    financingTermMonths: rawQuote.financing_term_months || 60,
    financingDownPayment: rawQuote.financing_down_payment || 0.00,
    
    // HVAC P0: Rebates data
    rebates: rawQuote.rebates || [],
    
    // HVAC P0: Equipment metadata
    equipmentMetadata: rawQuote.equipment_metadata || {},
    
    // Transform tiers
    tiers: {
      good: {
        title: rawQuote.good_title,
        price: rawQuote.good_price,
        description: rawQuote.good_description,
        bulletPoints: rawQuote.good_bullet_points || []
      },
      better: {
        title: rawQuote.better_title,
        price: rawQuote.better_price,
        description: rawQuote.better_description,
        bulletPoints: rawQuote.better_bullet_points || []
      },
      best: {
        title: rawQuote.best_title,
        price: rawQuote.best_price,
        description: rawQuote.best_description,
        bulletPoints: rawQuote.best_bullet_points || []
      }
    },
    
    // Transform owner/profile data
    owner: rawQuote.profiles ? {
      id: rawQuote.profiles.id,
      name: rawQuote.profiles.full_name,
      businessName: rawQuote.profiles.business_name,
      avatarUrl: rawQuote.profiles.avatar_url,
      whiteLabelUnlocked: rawQuote.profiles.white_label_unlocked === true
    } : null
  };
};

export const quoteService = {
  // ==========================================
  // ANALYTICS FUNCTIONS (PRODUCTION-READY)
  // ==========================================
  
  getBusinessAnalytics: async (timeframe, userId) => {
    try {
      if (!userId) {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('No authenticated user');
        userId = user.id;
      }
      
      const { data, error } = await supabase.rpc('get_business_analytics', {
        timeframe_days: parseInt(timeframe, 10),
        business_user_id: userId
      });

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('❌ getBusinessAnalytics error:', error.message);
      return { success: false, data: null, error: error.message };
    }
  },

  getQuoteViewAnalytics: async (userId) => {
    try {
      console.log('📈 ANALYTICS: Getting quote view analytics for user:', userId);
      
      if (!userId) {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('No authenticated user');
        userId = user.id;
      }
      
      // Get quote view data directly from quotes table since views are stored there now
      const { data: quotes, error } = await supabase
        .from('quotes')
        .select(`
          id,
          unique_id,
          client_name,
          view_count,
          first_viewed_at,
          last_viewed_at,
          created_at,
          status
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('❌ ANALYTICS: Database error:', error);
        throw error;
      }

      // Transform the data to match the expected format for the dashboard
      const views = quotes.flatMap(quote => {
        if (quote.view_count > 0 && quote.first_viewed_at) {
          return {
            quote_id: quote.id,
            unique_id: quote.unique_id,
            viewed_at: quote.first_viewed_at,
            view_count: quote.view_count,
            last_viewed_at: quote.last_viewed_at
          };
        }
        return [];
      });

      console.log('✅ ANALYTICS: Retrieved', views.length, 'quote views');
      return { success: true, data: { views } };
    } catch (error) {
      console.error('❌ ANALYTICS: getQuoteViewAnalytics error:', error.message);
      return { success: false, data: { views: [] }, error: error.message };
    }
  },
  
  // ==========================================
  // EXISTING QUOTE & OTHER FUNCTIONS
  // ==========================================

  getQuotes: async (bypassCache = false) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user');

      console.log(`🔄 QUOTES: Fetching quotes with view data ${bypassCache ? '(cache bypass)' : '(normal)'}`);
      
      // 🚀 STEP 1: Get all quotes first
      const { data: quotes, error: quotesError } = await supabase
        .from('quotes')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (quotesError) {
        console.error('❌ QUOTES: Failed to fetch quotes:', quotesError.message);
        throw quotesError;
      }

      if (!quotes || quotes.length === 0) {
        console.log('✅ QUOTES: No quotes found for user');
        return { success: true, data: [] };
      }

      console.log(`📋 QUOTES: Found ${quotes.length} quotes, now fetching view data...`);

      // 🚀 STEP 2: Get view data for all quotes
      try {
        const { data: allViews, error: viewsError } = await supabase
          .from('quote_views')
          .select('quote_id, viewed_at, first_view')
          .in('quote_id', quotes.map(q => q.id));

        if (viewsError) {
          console.warn('⚠️ QUOTES: Could not fetch view data, continuing without:', viewsError.message);
        }

        // 📊 STEP 3: Aggregate view data by quote_id
        const viewsByQuote = {};
        if (allViews && allViews.length > 0) {
          allViews.forEach(view => {
            if (!viewsByQuote[view.quote_id]) {
              viewsByQuote[view.quote_id] = [];
            }
            viewsByQuote[view.quote_id].push(view);
          });
          console.log(`📋 QUOTES: Processed view data for ${Object.keys(viewsByQuote).length} quotes`);
        } else {
          console.log('📋 QUOTES: No view data found (normal for new quotes)');
        }

        // 🚀 STEP 4: Combine quotes with their view data
        const quotesWithViews = quotes.map(quote => {
          const views = viewsByQuote[quote.id] || [];
          const viewCount = views.length;
          const firstViewedAt = views.length > 0 ? 
            views.reduce((earliest, view) => 
              new Date(view.viewed_at) < new Date(earliest.viewed_at) ? view : earliest
            ).viewed_at : null;
          const lastViewedAt = views.length > 0 ? 
            views.reduce((latest, view) => 
              new Date(view.viewed_at) > new Date(latest.viewed_at) ? view : latest
            ).viewed_at : null;
          
          return {
            ...quote,
            view_count: viewCount,
            first_viewed_at: firstViewedAt,
            last_viewed_at: lastViewedAt,
            viewed_at_list: views.map(v => v.viewed_at) // ← NEW: Keep all timestamps
          };
        });

        const transformedQuotes = quotesWithViews.map(transformQuoteData);
        console.log(`✅ QUOTES: Successfully loaded ${transformedQuotes.length} quotes with view data`);
        
        // 📋 DEBUG: Log view data for first few quotes
        if (transformedQuotes.length > 0) {
          console.log('🔍 QUOTES: View data sample:', 
            transformedQuotes.slice(0, 3).map(q => ({
              clientName: q.clientName,
              viewCount: q.viewCount,
              firstViewedAt: q.firstViewedAt,
              lastViewedAt: q.lastViewedAt
            }))
          );
        }
        
        return { success: true, data: transformedQuotes };
        
      } catch (viewError) {
        console.warn('⚠️ QUOTES: View data fetch failed, returning quotes without view data:', viewError.message);
        
        // Return quotes without view data if view fetching fails
        const transformedQuotes = quotes.map(quote => ({
          ...transformQuoteData(quote),
          viewCount: 0,
          firstViewedAt: null,
          lastViewedAt: null
        }));
        
        console.log(`✅ QUOTES: Fallback successful - loaded ${transformedQuotes.length} quotes (no view data)`);
        return { success: true, data: transformedQuotes };
      }
      
      return { success: true, data: transformedQuotes };
    } catch (error) {
      console.error('❌ getQuotes error:', error);
      return { success: false, data: [], error: error.message };
    }
  },

  createQuote: async (quoteData) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user');

      console.log('📅 DEBUG: Raw quote data received:', quoteData);
      
      // 🔧 ROBUST PRICE CONVERSION
      const convertPrice = (priceInput, tierName) => {
        const priceStr = String(priceInput || '0').trim();
        const parsed = parseFloat(priceStr);
        const finalPrice = isNaN(parsed) ? 0 : Math.max(0, parsed); // Ensure non-negative
        console.log(`💰 ${tierName} price: "${priceInput}" → "${priceStr}" → ${parsed} → ${finalPrice}`);
        return finalPrice;
      };

      const uniqueId = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
      const insertData = {
        user_id: user.id,
        unique_id: uniqueId,
        client_name: quoteData.clientName?.trim() || 'Client',
        client_email: quoteData.clientEmail?.trim() || 'client@example.com',
        status: 'pending',
        interactive_enabled: quoteData.interactiveEnabled || false,
        // HVAC P0: Monthly payment fields
        show_monthly_payments: quoteData.showMonthlyPayments || false,
        financing_apr: parseFloat(quoteData.financingApr) || 0.00,
        financing_term_months: parseInt(quoteData.financingTermMonths) || 60,
        financing_down_payment: parseFloat(quoteData.financingDownPayment) || 0.00,
        good_title: quoteData.tiers?.good?.title?.trim() || 'Good',
        good_price: convertPrice(quoteData.tiers?.good?.price, 'Good'),
        good_description: quoteData.tiers?.good?.description?.trim() || '',
        good_bullet_points: quoteData.tiers?.good?.bulletPoints?.filter(bp => bp?.trim()) || [],
        better_title: quoteData.tiers?.better?.title?.trim() || 'Better',
        better_price: convertPrice(quoteData.tiers?.better?.price, 'Better'),
        better_description: quoteData.tiers?.better?.description?.trim() || '',
        better_bullet_points: quoteData.tiers?.better?.bulletPoints?.filter(bp => bp?.trim()) || [],
        best_title: quoteData.tiers?.best?.title?.trim() || 'Best',
        best_price: convertPrice(quoteData.tiers?.best?.price, 'Best'),
        best_description: quoteData.tiers?.best?.description?.trim() || '',
        best_bullet_points: quoteData.tiers?.best?.bulletPoints?.filter(bp => bp?.trim()) || [],
        // HVAC P0: Equipment metadata for all tiers
        equipment_metadata: {
          good: quoteData.tiers?.good?.equipment || {},
          better: quoteData.tiers?.better?.equipment || {},
          best: quoteData.tiers?.best?.equipment || {}
        }
      };
      
      console.log('📋 DEBUG: Data being sent to database:', insertData);

      const { data, error } = await supabase.from('quotes').insert(insertData).select().single();
      if (error) throw error;
      
      // HVAC P0: Handle rebates if they exist
      if (quoteData.rebates && quoteData.rebates.length > 0) {
        console.log('💰 Creating rebates for quote:', quoteData.rebates);
        const rebateInserts = quoteData.rebates.map(rebate => ({
          quote_id: data.id,
          label: rebate.label?.trim() || 'Rebate',
          amount_type: rebate.amountType || 'fixed',
          amount_value: parseFloat(rebate.amountValue) || 0,
          notes: rebate.notes?.trim() || null
        }));
        
        const { error: rebateError } = await supabase.from('quote_rebates').insert(rebateInserts);
        if (rebateError) {
          console.error('❌ Failed to create rebates:', rebateError);
          // Don't fail the whole quote creation if rebates fail
        } else {
          console.log('✅ Created rebates successfully');
        }
      }
      
      return { success: true, data: transformQuoteData(data) };
    } catch (error) {
      console.error('❌ createQuote error:', error);
      throw new Error(error.message || 'Failed to create quote');
    }
  },

  deleteQuote: async (quoteId) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user');
      const { error } = await supabase.from('quotes').delete().eq('id', quoteId).eq('user_id', user.id);
      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('❌ deleteQuote error:', error);
      throw new Error(error.message || 'Failed to delete quote');
    }
  },
  
  getQuoteByUniqueId: async (uniqueId) => {
    // 🚨 USE DIRECT FETCH INSTEAD OF SUPABASE SDK
    console.log('🔄 USING DIRECT FETCH METHOD TO BYPASS SDK ISSUES');
    
    try {
      console.log(`🔍 DIRECT FETCH: Getting quote ${uniqueId} via raw fetch...`);
      
      const url = `${process.env.REACT_APP_SUPABASE_URL}/rest/v1/quotes?unique_id=eq.${uniqueId}&select=*`;
      const headers = {
        'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      };
      
      console.log('🔍 Fetch URL:', url);
      console.log('🔍 Headers set:', Object.keys(headers));
      
      const response = await Promise.race([
        fetch(url, { method: 'GET', headers }),
        new Promise((_, reject) => setTimeout(() => reject(new Error('Direct fetch timeout')), 8000))
      ]);
      
      console.log('📡 Direct fetch response:', response.status, response.statusText);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('📄 Raw quote data:', data);
      
      if (!data || data.length === 0) {
        throw new Error('Quote not found');
      }
      
      const quote = data[0]; // PostgreSQL returns array
      
      // Now try to get profile data separately
      let profileData = null;
      try {
        const profileUrl = `${process.env.REACT_APP_SUPABASE_URL}/rest/v1/profiles?id=eq.${quote.user_id}&select=*`;
        const profileResponse = await Promise.race([
          fetch(profileUrl, { method: 'GET', headers }),
          new Promise((_, reject) => setTimeout(() => reject(new Error('Profile fetch timeout')), 5000))
        ]);
        
        if (profileResponse.ok) {
          const profileArray = await profileResponse.json();
          profileData = profileArray[0] || null;
          console.log('👤 Profile data loaded:', !!profileData);
        }
      } catch (profileError) {
        console.warn('⚠️ Profile fetch failed, continuing without:', profileError.message);
      }
      
      // HVAC P0: Fetch rebates data
      let rebatesData = [];
      try {
        const rebatesUrl = `${process.env.REACT_APP_SUPABASE_URL}/rest/v1/quote_rebates?quote_id=eq.${quote.id}&select=*`;
        const rebatesResponse = await Promise.race([
          fetch(rebatesUrl, { method: 'GET', headers }),
          new Promise((_, reject) => setTimeout(() => reject(new Error('Rebates fetch timeout')), 5000))
        ]);
        
        if (rebatesResponse.ok) {
          rebatesData = await rebatesResponse.json();
          console.log('💰 Rebates data loaded:', rebatesData.length);
        }
      } catch (rebatesError) {
        console.warn('⚠️ Rebates fetch failed, continuing without:', rebatesError.message);
      }
      
      // Combine quote with profile and rebates data
      const quoteWithProfile = {
        ...quote,
        profiles: profileData,
        rebates: rebatesData
      };
      
      const transformedData = transformQuoteData(quoteWithProfile);
      console.log('✅ Direct fetch SUCCESS:', {
        id: transformedData.id,
        clientName: transformedData.clientName,
        hasOwner: !!transformedData.owner
      });
      
      return { success: true, data: transformedData };
      
    } catch (error) {
      console.error('❌ Direct fetch failed:', error.message);
      return { success: false, error: error.message, data: null };
    }
  },

  getQuoteByUniqueIdOld: async (uniqueId) => {
    try {
      console.log(`🔍 getQuoteByUniqueId: Fetching quote with uniqueId: ${uniqueId}`);
      
      // 🔍 Declare quote variable at function scope
      let quote = null;
      
      // 🔍 COMPREHENSIVE CONNECTIVITY DIAGNOSTIC
      console.log('🔍 🏥 COMPREHENSIVE SUPABASE DIAGNOSTIC STARTING...');
      
      // Check 1: Environment variables
      console.log('🔍 CHECK 1: Environment Variables');
      console.log('  - SUPABASE_URL:', process.env.REACT_APP_SUPABASE_URL ? 'SET' : 'MISSING');
      console.log('  - SUPABASE_ANON_KEY:', process.env.REACT_APP_SUPABASE_ANON_KEY ? 'SET' : 'MISSING');
      console.log('  - URL value:', process.env.REACT_APP_SUPABASE_URL);
      
      // Check 2: Supabase client creation
      console.log('🔍 CHECK 2: Supabase Client');
      console.log('  - Client exists:', !!supabase);
      console.log('  - Client URL:', supabase.supabaseUrl);
      
      // Check 3: Simple network test
      console.log('🔍 CHECK 3: Basic Network Test (fetch)');
      try {
        const response = await Promise.race([
          fetch(process.env.REACT_APP_SUPABASE_URL + '/rest/v1/', {
            method: 'GET',
            headers: {
              'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
              'Content-Type': 'application/json'
            }
          }),
          new Promise((_, reject) => setTimeout(() => reject(new Error('Network timeout')), 5000))
        ]);
        console.log('✅ Network fetch successful:', response.status, response.statusText);
      } catch (networkError) {
        console.error('❌ Network fetch failed:', networkError.message);
      }
      
      // Check 4: Supabase health check
      console.log('🔍 CHECK 4: Supabase Health Check');
      try {
        const healthCheck = await Promise.race([
          supabase.from('quotes').select('count', { count: 'exact', head: true }),
          new Promise((_, reject) => setTimeout(() => reject(new Error('Health check timeout')), 5000))
        ]);
        console.log('✅ Supabase health check passed:', healthCheck);
      } catch (healthError) {
        console.error('❌ Supabase health check failed:', healthError.message);
        
        // If health check fails, try a simpler approach
        console.log('🔍 CHECK 5: Fallback - Simple Table Access');
        try {
          const simpleTest = await Promise.race([
            supabase.rpc('version'),
            new Promise((_, reject) => setTimeout(() => reject(new Error('Version timeout')), 3000))
          ]);
          console.log('✅ Simple RPC call worked:', simpleTest);
        } catch (rpcError) {
          console.error('❌ RPC call also failed:', rpcError.message);
          console.error('🚨 COMPLETE CONNECTIVITY FAILURE - Supabase is unreachable');
          throw new Error('Supabase connection failed - service may be down or network issues');
        }
      }
      
      // If we get here, basic connectivity works, now try quote fetch
      console.log('🔍 ✅ Basic connectivity verified, proceeding with quote fetch...');
      
      // 🔍 STEP 2: Try quote query WITHOUT profiles join first
      console.log('🔍 STEP 2: Trying quote query without profiles join...');
      const simpleQueryPromise = supabase
        .from('quotes')
        .select('*')
        .eq('unique_id', uniqueId)
        .single();
      
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Quote fetch timeout')), 8000) // Increased timeout
      );
      
      const { data: quoteData, error } = await Promise.race([
        simpleQueryPromise,
        timeoutPromise
      ]);
      
      console.log('🔍 Simple quote query result:', { 
        found: !!quoteData, 
        error: error?.message || null,
        quoteId: quoteData?.id || 'none'
      });
      
      if (error) {
        console.error('🚨 Simple query error details:');
        console.error('  - Error message:', error.message);
        console.error('  - Error code:', error.code);
        console.error('  - Error details:', error.details);
        console.error('  - Error hint:', error.hint);
        
        // 🔍 STEP 3: Try with anon access (RLS bypass) 
        console.log('🔍 STEP 3: Trying with anon access (RLS bypass)...');
        try {
          const { data: anonQuote, error: anonError } = await Promise.race([
            supabase.from('quotes').select('*').eq('unique_id', uniqueId).single(),
            new Promise((_, reject) => setTimeout(() => reject(new Error('Anon timeout')), 3000))
          ]);
          
          if (anonError) {
            console.error('🚨 Anon query error details:');
            console.error('  - Anon error message:', anonError.message);
            console.error('  - Anon error code:', anonError.code);
          }
          
          if (anonError || !anonQuote) {
            console.error('🚨 Anon query also failed');
            throw error; // Use original error
          }
          
          console.log('✅ Anon query succeeded! RLS was the issue.');
          quote = anonQuote;
          
        } catch (anonError) {
          console.error('🚨 Both queries failed - this indicates database/network issue');
          throw error; // Use original error
        }
      } else {
        quote = quoteData;
      }
      
      if (!quote) {
        console.error('🚨 No quote found with uniqueId:', uniqueId);
        throw new Error('Quote not found');
      }
      
      // 🔍 STEP 4: Try to get profile data separately if quote found
      console.log('🔍 STEP 4: Quote found! Fetching profile separately...');
      let profileData = null;
      
      try {
        const { data: profile, error: profileError } = await Promise.race([
          supabase.from('profiles').select('*').eq('id', quote.user_id).single(),
          new Promise((_, reject) => setTimeout(() => reject(new Error('Profile timeout')), 3000))
        ]);
        
        if (profile && !profileError) {
          profileData = profile;
          console.log('✅ Profile loaded successfully');
        } else {
          console.warn('⚠️ Profile fetch failed, continuing without profile:', profileError?.message);
        }
      } catch (profileError) {
        console.warn('⚠️ Profile fetch timed out, continuing without profile');
      }
      
      // 🔍 Transform data with separate profile
      const quoteWithProfile = {
        ...quote,
        profiles: profileData
      };
      
      const transformedData = transformQuoteData(quoteWithProfile);
      console.log('✅ Quote successfully transformed:', {
        id: transformedData.id,
        clientName: transformedData.clientName,
        hasOwner: !!transformedData.owner
      });
      
      return { success: true, data: transformedData };
    } catch (error) {
      console.error('❌ getQuoteByUniqueId error:', error.message);
      return { 
        success: false, 
        error: error.message,
        // 🚨 FALLBACK: Return minimal data to prevent app crash
        data: {
          id: 'fallback',
          uniqueId: uniqueId,
          clientName: 'Sample Client',
          clientEmail: 'client@example.com',
          status: 'pending',
          createdAt: new Date().toISOString(),
          interactiveEnabled: false,
          tiers: {
            good: { title: 'Basic Service', price: 100, description: 'Sample service', bulletPoints: [] },
            better: { title: 'Standard Service', price: 200, description: 'Sample service', bulletPoints: [] },
            best: { title: 'Premium Service', price: 300, description: 'Sample service', bulletPoints: [] }
          },
          owner: {
            name: 'Sample Business',
            businessName: 'Sample Business Inc',
            avatarUrl: null
          }
        }
      };
    }
  },
  
  acceptQuote: async (uniqueId, acceptanceData) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const signatureName = acceptanceData.signatureName || acceptanceData.clientSignatureName;

      const response = await fetch(`${apiUrl}/api/quotes/accept`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          uniqueId,
          acceptanceData: {
            ...acceptanceData,
            signatureName,
          }
        }),
      });

      const result = await response.json().catch(() => ({}));
      if (!response.ok || !result.success) {
        throw new Error(result.error || result.message || 'Failed to accept quote');
      }

      return { success: true, data: transformQuoteData(result.data) };
      
    } catch (error) {
      console.error('❌ acceptQuote error:', error);
      throw new Error(error.message || 'Failed to accept quote');
    }
  },
  
  // In quoteService.js
  trackQuoteView: async (uniqueId, currentUserId = null) => {
    try {
      console.log('📊 TRACKING: Starting quote view tracking for:', uniqueId);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!currentUserId && user) {
        currentUserId = user.id;
      }

      // Get quote data to check ownership
      const { data: quote } = await supabase.from('quotes').select('user_id').eq('unique_id', uniqueId).single();
      if (!quote) {
        console.warn('⚠️ TRACKING: Quote not found for unique_id:', uniqueId);
        return { success: true, message: 'Quote not found - skipping tracking', ownerView: false };
      }

      // Check if this is the quote owner viewing their own quote
      if (currentUserId && quote.user_id === currentUserId) {
        console.log('👤 TRACKING: Owner view detected - skipping tracking');
        return { success: true, message: 'Owner view - not tracked', ownerView: true };
      }

      // Track the view using the database function with SIMPLE parameters
      console.log('📈 TRACKING: Recording client view in database...');
      
      // 🚑 BULLETPROOF: Try the database function with timeout
      const trackingPromise = supabase.rpc('track_quote_view', {
        p_unique_id: uniqueId,
        p_viewer_user_agent: navigator.userAgent || 'Unknown'
      });
      
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Tracking timeout')), 3000)
      );
      
      const { data: trackingResult, error: trackingError } = await Promise.race([
        trackingPromise,
        timeoutPromise
      ]);

      if (trackingError) {
        console.warn('⚠️ TRACKING: Database function failed, using fallback:', trackingError.message);
        
        // 🚑 FALLBACK: Direct database update if function fails
        try {
          const { error: fallbackError } = await supabase
            .from('quotes')
            .update({
              view_count: 1, // Simple fallback - just set to 1
              last_viewed_at: new Date().toISOString()
            })
            .eq('unique_id', uniqueId)
            .eq('view_count', null); // Only update if view_count is null (first view)
          
          if (!fallbackError) {
            console.log('✅ TRACKING: Fallback tracking successful');
            return { success: true, message: 'Tracked via fallback method' };
          }
        } catch (fallbackError) {
          console.warn('⚠️ TRACKING: Even fallback failed, but continuing');
        }
        
        // Even if tracking completely fails, don't break the quote view
        return { success: true, message: 'Tracking failed but quote view continues' };
      }

      console.log('✅ TRACKING: View recorded successfully:', trackingResult);
      return { 
        success: true, 
        message: 'View tracked successfully',
        data: trackingResult
      };
    } catch (error) {
      console.warn('⚠️ TRACKING: Complete tracking failure, but not blocking quote view:', error.message);
      return { success: true, message: 'Tracking failed but quote view continues' };
    }
  },

  getInteractiveProducts: async (userId) => {
    try {
      console.log('🔍 PRODUCTS: Getting interactive products for user:', userId);
      
      // ⚡ STEP 1: Quick connectivity test first
      console.log('🔍 PRODUCTS: Testing basic connectivity...');
      const connectivityTest = await Promise.race([
        supabase.from('profiles').select('id').eq('id', userId).limit(1),
        new Promise((_, reject) => setTimeout(() => reject(new Error('Connectivity timeout')), 2000))
      ]);
      
      if (!connectivityTest || connectivityTest.error) {
        console.warn('⚠️ PRODUCTS: Basic connectivity failed, returning empty products');
        return { success: true, data: [] };
      }
      
      console.log('✅ PRODUCTS: Basic connectivity confirmed');
      
      // ⚡ STEP 2: Check if interactive_products table exists
      console.log('🔍 PRODUCTS: Checking if interactive_products table exists...');
      const tableTest = await Promise.race([
        supabase.from('interactive_products').select('count', { count: 'exact', head: true }),
        new Promise((_, reject) => setTimeout(() => reject(new Error('Table check timeout')), 2000))
      ]);
      
      if (tableTest.error) {
        console.warn('⚠️ PRODUCTS: interactive_products table not accessible:', tableTest.error.message);
        if (tableTest.error.message.includes('does not exist') || tableTest.error.message.includes('permission denied')) {
          console.log('📄 PRODUCTS: Table missing or no access - returning empty products list');
          return { success: true, data: [] };
        }
      }
      
      console.log('✅ PRODUCTS: interactive_products table exists and accessible');
      
      // ⚡ STEP 3: Simple products query with timeout
      console.log('🔍 PRODUCTS: Fetching products with aggressive timeout...');
      const productsQuery = supabase
        .from('interactive_products')
        .select(`
          id,
          name,
          description,
          base_price,
          unit_name,
          is_active,
          category_id,
          display_order
        `)
        .eq('user_id', userId)
        .eq('is_active', true)
        .order('display_order')
        .limit(50); // Limit results to prevent massive queries
      
      const { data: products, error: productsError } = await Promise.race([
        productsQuery,
        new Promise((_, reject) => setTimeout(() => reject(new Error('Products query timeout')), 3000))
      ]);
      
      if (productsError) {
        console.error('❌ PRODUCTS: Query error:', productsError.message);
        if (productsError.message.includes('timeout')) {
          console.warn('⏰ PRODUCTS: Query timed out - returning empty list');
          return { success: true, data: [] };
        }
        throw productsError;
      }
      
      if (!products || products.length === 0) {
        console.log('📄 PRODUCTS: No products found for user - this is normal for new users');
        return { success: true, data: [] };
      }
      
      console.log('✅ PRODUCTS: Retrieved', products.length, 'products');
      
      // ⚡ STEP 4: Get categories (with timeout and fallback)
      console.log('🔍 PRODUCTS: Fetching categories...');
      let categories = {};
      try {
        const { data: categoryData, error: categoryError } = await Promise.race([
          supabase
            .from('product_categories')
            .select('id, name')
            .eq('user_id', userId),
          new Promise((_, reject) => setTimeout(() => reject(new Error('Categories timeout')), 2000))
        ]);
        
        if (!categoryError && categoryData) {
          categoryData.forEach(cat => {
            categories[cat.id] = cat.name;
          });
          console.log('✅ PRODUCTS: Categories loaded:', Object.keys(categories).length);
        } else {
          console.warn('⚠️ PRODUCTS: Categories failed to load, continuing without:', categoryError?.message);
        }
      } catch (categoryError) {
        console.warn('⚠️ PRODUCTS: Categories timed out, continuing without them');
      }
      
      // ⚡ STEP 5: Get pricing tiers (with timeout and fallback)
      console.log('🔍 PRODUCTS: Fetching pricing tiers...');
      const productsWithTiers = await Promise.all(
        products.map(async (product) => {
          let pricingTiers = [];
          
          try {
            const { data: tiers, error: tiersError } = await Promise.race([
              supabase
                .from('pricing_tiers')
                .select('*')
                .eq('product_id', product.id)
                .order('min_quantity')
                .limit(10), // Limit tiers per product
              new Promise((_, reject) => setTimeout(() => reject(new Error('Tiers timeout')), 1000))
            ]);
            
            if (!tiersError && tiers) {
              pricingTiers = tiers;
            } else {
              console.warn(`⚠️ PRODUCTS: Pricing tiers failed for product ${product.id}:`, tiersError?.message);
            }
          } catch (tierError) {
            console.warn(`⚠️ PRODUCTS: Pricing tiers timed out for product ${product.id}`);
          }
          
          return {
            ...product,
            category_name: categories[product.category_id] || null,
            pricing_tiers: pricingTiers
          };
        })
      );
      
      console.log('✅ PRODUCTS: Successfully processed', productsWithTiers.length, 'products with tiers and categories');
      return { success: true, data: productsWithTiers };
      
    } catch (error) {
      console.error('❌ PRODUCTS: getInteractiveProducts error:', error.message);
      
      // Don't throw errors for products - just return empty list
      if (error.message.includes('timeout')) {
        console.warn('⏰ PRODUCTS: Complete timeout - returning empty product list');
      } else {
        console.warn('❌ PRODUCTS: Database error - returning empty product list');
      }
      
      return { success: true, data: [] };
    }
  },

  createInteractiveProduct: async (productData) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user');
      const insertData = { ...productData, user_id: user.id };
      const { data, error } = await supabase.from('interactive_products').insert(insertData).select().single();
      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('❌ createInteractiveProduct error:', error);
      throw new Error(error.message || 'Failed to create product');
    }
  },

  deleteInteractiveProduct: async (productId) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user');
      const { error } = await supabase.from('interactive_products').delete().eq('id', productId).eq('user_id', user.id);
      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('❌ deleteInteractiveProduct error:', error);
      throw new Error(error.message || 'Failed to delete product');
    }
  },

  getProductCategories: async (userId) => {
    try {
      // FIXED: Accept userId parameter or get from auth
      let targetUserId = userId;
      if (!targetUserId) {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('No authenticated user');
        targetUserId = user.id;
      }
      
      const { data, error } = await supabase
        .from('product_categories')
        .select('*')
        .eq('user_id', targetUserId)
        .order('display_order');
        
      if (error) throw error;
      console.log('✅ CATEGORIES: Retrieved', data?.length || 0, 'categories');
      return { success: true, data: data || [] };
    } catch (error) {
      console.error('❌ getProductCategories error:', error);
      return { success: false, data: [], error: error.message };
    }
  },

  createProductCategory: async (categoryData) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user');
      const insertData = { ...categoryData, user_id: user.id };
      const { data, error } = await supabase.from('product_categories').insert(insertData).select().single();
      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('❌ createProductCategory error:', error);
      throw new Error(error.message || 'Failed to create category');
    }
  },

  deleteProductCategory: async (categoryId) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user');
      const { error } = await supabase.from('product_categories').delete().eq('id', categoryId).eq('user_id', user.id);
      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('❌ deleteProductCategory error:', error);
      throw new Error(error.message || 'Failed to delete category');
    }
  },

  loadPricingPresets: async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user');
      const { data, error } = await supabase.from('pricing_presets').select('*').eq('user_id', user.id).order('created_at');
      if (error) throw error;
      return { success: true, data: data || [] };
    } catch (error) {
      console.error('❌ loadPricingPresets error:', error);
      return { success: false, data: [], error: error.message };
    }
  },

  savePricingPresets: async (presetData) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user');
      
      // Transform the frontend tier data to match database schema
      const insertData = {
        user_id: user.id,
        name: presetData.name,
        description: presetData.description,
        is_default: presetData.isDefault || false,
        good_title: presetData.tiers?.good?.title,
        good_price: presetData.tiers?.good?.price,
        good_description: presetData.tiers?.good?.description,
        good_bullet_points: presetData.tiers?.good?.bulletPoints || [],
        better_title: presetData.tiers?.better?.title,
        better_price: presetData.tiers?.better?.price,
        better_description: presetData.tiers?.better?.description,
        better_bullet_points: presetData.tiers?.better?.bulletPoints || [],
        best_title: presetData.tiers?.best?.title,
        best_price: presetData.tiers?.best?.price,
        best_description: presetData.tiers?.best?.description,
        best_bullet_points: presetData.tiers?.best?.bulletPoints || []
      };
      
      const { data, error } = await supabase.from('pricing_presets').insert(insertData).select().single();
      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('❌ savePricingPresets error:', error);
      throw new Error(error.message || 'Failed to save preset');
    }
  },

  getPresetDetails: async (presetId) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user');
      
      const { data, error } = await supabase
        .from('pricing_presets')
        .select('*')
        .eq('id', presetId)
        .eq('user_id', user.id)
        .single();
        
      if (error) throw error;
      if (!data) throw new Error('Preset not found');
      
      return { success: true, data };
    } catch (error) {
      console.error('❌ getPresetDetails error:', error);
      throw new Error(error.message || 'Failed to get preset details');
    }
  },

  deletePricingPresets: async (presetId) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user');
      
      const { error } = await supabase
        .from('pricing_presets')
        .delete()
        .eq('id', presetId)
        .eq('user_id', user.id);
        
      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('❌ deletePricingPresets error:', error);
      throw new Error(error.message || 'Failed to delete preset');
    }
  },

  createPricingTiers: async (productId, tiersArray) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user');
      if (!Array.isArray(tiersArray) || tiersArray.length === 0) throw new Error('Valid pricing tiers array is required');
      const tierInserts = tiersArray.map(tier => ({ product_id: productId, ...tier }));
      const { data, error } = await supabase.from('pricing_tiers').insert(tierInserts).select();
      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('❌ createPricingTiers error:', error);
      throw new Error(error.message || 'Failed to create pricing tiers');
    }
  },

  calculateOptimalPrice: (pricingTiers, quantity, basePrice) => {
    try {
      if (!pricingTiers || !Array.isArray(pricingTiers) || quantity <= 0) {
        return { price_per_unit: basePrice || 0, total_price: (basePrice || 0) * Math.max(quantity, 0), savings: 0 };
      }
      const applicableTier = pricingTiers.find(tier => {
        const minQty = tier.min_quantity || 1;
        const maxQty = tier.max_quantity;
        return maxQty === null || maxQty === undefined ? quantity >= minQty : quantity >= minQty && quantity <= maxQty;
      });
      if (!applicableTier) {
        return { price_per_unit: basePrice || 0, total_price: (basePrice || 0) * quantity, savings: 0 };
      }
      const pricePerUnit = applicableTier.price_per_unit || basePrice || 0;
      const totalPrice = pricePerUnit * quantity;
      const savings = Math.max(0, (basePrice || 0) * quantity - totalPrice);
      return { price_per_unit: pricePerUnit, total_price: totalPrice, savings: savings };
    } catch (error) {
      console.error('❌ calculateOptimalPrice error:', error);
      return { price_per_unit: basePrice || 0, total_price: (basePrice || 0) * Math.max(quantity, 0), savings: 0 };
    }
  }
};

export const calculateOptimalPrice = quoteService.calculateOptimalPrice;
